import type { ChatMessage } from '@/api/chat';

/**
 * Соединение чата: один владелец сокета, который сам его восстанавливает (02.10.2026, сборка 1.1.1).
 *
 * ⚠️ ЗАЧЕМ. До этого экран открывал сокет один раз и на обрыв только забывал ссылку. Телефон рвёт соединение
 * постоянно: приложение ушло в фон, сменилась сеть, лифт. После первого же обрыва переписка молча переставала
 * обновляться до перезахода на экран, а сервер пропущенного не досылает — при подключении он повторяет только
 * присутствие собеседника (`chat-service/internal/service/hub.go`).
 *
 * ПОРЯДОК ПОДКЛЮЧЕНИЯ — СНАЧАЛА ИСТОРИЯ ПО REST, ПОТОМ СОКЕТ. Причин две:
 *  - токен живёт 15 минут и проверяется только на рукопожатии; отказ рукопожатия странице не виден (закрытие
 *    с кодом 1006 без причины). Запрос истории идёт через обычный клиент, а тот на 401 сам продлевает токен;
 *  - история приносит то, что пришло, пока соединения не было.
 * После открытия сокета история спрашивается ещё раз: между первым ответом и открытием есть щель.
 *
 * Модуль без React и без браузера — зависимости приходят снаружи, поведение проверяется в `chatSocket.test.ts`.
 */

/** Паузы между попытками, мс. После успешного открытия счёт начинается заново. */
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000] as const;

/**
 * Сколько приложение может пробыть в фоне, чтобы открытому сокету ещё верить. iOS замораживает соединение и
 * о его смерти не сообщает: `readyState` остаётся «открыт», а кадры не ходят.
 */
export const STALE_AFTER_HIDDEN_MS = 30_000;

const CONNECTING = 0;
const OPEN = 1;

/** То, что нужно от `WebSocket`: в проверках подставляется свой объект. */
export type SocketLike = {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((e: unknown) => void) | null;
  onmessage: ((e: { data: unknown }) => void) | null;
  onclose: ((e: unknown) => void) | null;
  onerror: ((e: unknown) => void) | null;
};

/**
 * Кадр сокета → события.
 *
 * ⚠️ В ОДНОМ КАДРЕ БЫВАЕТ НЕСКОЛЬКО СОБЫТИЙ. Сервер склеивает очередь через перевод строки (`WritePump` в
 * `hub.go`). Один `JSON.parse` на весь кадр падал, и сообщения, пришедшие пачкой, пропадали молча — как раз
 * тогда, когда их много: после паузы или при быстрой переписке.
 */
export function splitFrames(data: unknown): unknown[] {
  if (typeof data !== 'string') return [];
  const out: unknown[] = [];
  for (const line of data.split('\n')) {
    if (!line.trim()) continue;
    try {
      out.push(JSON.parse(line));
    } catch {
      /* не JSON — пропускаем эту строку, остальные целы */
    }
  }
  return out;
}

const isTmp = (m: ChatMessage) => m.id.startsWith('tmp-');

/**
 * Лента + ответ истории → лента.
 *
 * Ничего не теряется и не двоится: сообщение узнаётся по `id`; своё неотправленное (`tmp-…`) остаётся, пока в
 * истории не появится его настоящее — от того же отправителя и с тем же текстом.
 */
export function mergeMessages(current: ChatMessage[], fetched: ChatMessage[]): ChatMessage[] {
  const known = new Set(current.map((m) => m.id));
  const fresh = fetched.filter((m) => !known.has(m.id));
  // Каждое новое настоящее сообщение «гасит» не больше одного своего неотправленного.
  const spent = new Set<string>();
  for (const real of fresh) {
    const tmp = current.find(
      (m) => isTmp(m) && !spent.has(m.id) && m.sender_id === real.sender_id && m.content === real.content,
    );
    if (tmp) spent.add(tmp.id);
  }
  const byId = new Map(fetched.map((m) => [m.id, m]));
  const merged = [
    // Известное сообщение берём из свежего ответа: в нём актуальная отметка «прочитано».
    ...current.filter((m) => !spent.has(m.id)).map((m) => byId.get(m.id) ?? m),
    ...fresh,
  ];
  const at = (m: ChatMessage) => {
    const t = Date.parse(m.created_at);
    return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
  };
  // Сортировка устойчивая: при равном времени порядок прежний.
  return merged.map((m, i) => ({ m, i })).sort((a, b) => at(a.m) - at(b.m) || a.i - b.i).map((x) => x.m);
}

type Timer = ReturnType<typeof setTimeout>;

export type ChatSocketDeps = {
  /** Открыть сокет. `null` — открыть нечем (нет токена). */
  open: () => SocketLike | null;
  /** Дозагрузить историю по REST. Отказ — причина повторить попытку позже. */
  catchUp: () => Promise<void>;
  onEvent: (event: unknown) => void;
  isOnline: () => boolean;
  /** Отказ, после которого повторять бессмысленно: права нет, диалога нет. */
  isFatal?: (err: unknown) => boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => Timer;
  clearTimer?: (t: Timer) => void;
};

export type ChatSocket = {
  start(): void;
  stop(): void;
  /** Экран вернулся или сеть появилась. `force` — открытому сокету не верить (сменилась сеть). */
  wake(force?: boolean): void;
  /** Приложение ушло в фон, вкладка скрыта. */
  hidden(): void;
  /** Сети нет: попытки по таймеру останавливаются до `wake`. */
  offline(): void;
  /** Отправить строку. `false` — сокет не открыт, отправлять другим путём. */
  send(data: string): boolean;
  isOpen(): boolean;
};

export function createChatSocket(deps: ChatSocketDeps): ChatSocket {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));

  let stopped = true;
  let socket: SocketLike | null = null;
  let busy = false; // история в пути — второй попытки рядом не начинаем
  let attempt = 0;
  let timer: Timer | null = null;
  let hiddenAt: number | null = null;
  let epoch = 0; // растёт при остановке: ответы прошлой жизни не трогают новую

  const clear = () => {
    if (timer != null) {
      clearTimer(timer);
      timer = null;
    }
  };

  const retry = () => {
    if (stopped || timer != null) return;
    const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
    attempt += 1;
    timer = setTimer(() => {
      timer = null;
      void connect();
    }, delay);
  };

  /** Бросить сокет без повтора: обработчики снимаются раньше `close`, чтобы обрыв не запустил таймер. */
  const drop = () => {
    const ws = socket;
    socket = null;
    if (!ws) return;
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    try {
      ws.close();
    } catch {
      /* уже закрыт */
    }
  };

  async function connect(): Promise<void> {
    if (stopped || busy || socket) return;
    if (!deps.isOnline()) return; // без сети не стучимся — разбудит `wake`
    busy = true;
    const mine = epoch;
    try {
      await deps.catchUp();
    } catch (err) {
      busy = false;
      if (mine !== epoch) return;
      // Права нет или диалога нет — не долбим сервер отказами.
      if (deps.isFatal?.(err)) return;
      retry();
      return;
    }
    busy = false;
    if (stopped || mine !== epoch || socket) return;

    let ws: SocketLike | null = null;
    try {
      ws = deps.open();
    } catch {
      ws = null;
    }
    if (!ws) {
      retry();
      return;
    }
    const opened = ws;
    socket = opened;
    opened.onopen = () => {
      if (socket !== opened) return;
      attempt = 0;
      // Щель между ответом истории и открытием сокета.
      void deps.catchUp().catch(() => {});
    };
    opened.onmessage = (e) => {
      if (socket !== opened) return;
      for (const event of splitFrames(e.data)) deps.onEvent(event);
    };
    opened.onclose = () => {
      if (socket !== opened) return;
      socket = null;
      retry();
    };
    opened.onerror = () => {
      /* за ошибкой всегда идёт close — повтор запускается там */
    };
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      attempt = 0;
      void connect();
    },
    stop() {
      stopped = true;
      epoch += 1;
      busy = false;
      clear();
      drop();
    },
    wake(force = false) {
      if (stopped) return;
      const longHidden = hiddenAt != null && now() - hiddenAt > STALE_AFTER_HIDDEN_MS;
      hiddenAt = null;
      clear();
      attempt = 0;
      const alive = socket != null && (socket.readyState === OPEN || socket.readyState === CONNECTING);
      if (socket && (force || longHidden || !alive)) drop();
      if (socket) return; // соединение живое и свежее — оставляем
      void connect();
    },
    hidden() {
      if (hiddenAt == null) hiddenAt = now();
    },
    offline() {
      clear();
    },
    send(data) {
      if (!socket || socket.readyState !== OPEN) return false;
      try {
        socket.send(data);
        return true;
      } catch {
        return false;
      }
    },
    isOpen() {
      return socket != null && socket.readyState === OPEN;
    },
  };
}
